/**
 * Tahap 7D — Budget, Advanced Depreciation, Forecast, Exports.
 * 100% Bahasa Indonesia — konsisten dengan tema slate existing.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { formatRupiah } from "@/lib/format";
import {
  Wallet, TrendingUp, TrendingDown, ShieldCheck, Layers, FileSpreadsheet,
  Building2, Calculator, LineChart, PieChart, Download, Plus, Send, Check, X, Lock, Eye,
} from "lucide-react";
import { BarCard, HorizontalBarCard, COLORS as CHART_COLORS } from "@/components/charts/DashboardCharts";

/* RBAC client-side mirror (7D-G) */
const PERMS_7D = {
  super_admin: ["*"],
  keuangan: ["BUDGET_LIHAT", "BUDGET_CREATE", "BUDGET_EDIT",
              "ASET_LIHAT", "FORECAST_LIHAT", "FORECAST_GENERATE"],
  akuntansi: ["BUDGET_LIHAT", "BUDGET_CREATE", "BUDGET_EDIT", "BUDGET_REVIEW", "BUDGET_APPROVE",
               "ASET_LIHAT", "ASET_UOP_RECORD", "ASET_KELOLA", "ASET_DEPRECIATION",
               "FORECAST_LIHAT", "FORECAST_GENERATE"],
  direktur_utama: ["BUDGET_LIHAT", "BUDGET_APPROVE", "BUDGET_LOCK", "BUDGET_REJECT",
                    "ASET_LIHAT", "ASET_METODE_KELOLA", "FORECAST_LIHAT", "FORECAST_GENERATE"],
  direktur_operasional: ["BUDGET_LIHAT", "BUDGET_REVIEW", "ASET_LIHAT", "FORECAST_LIHAT"],
  auditor: ["BUDGET_LIHAT", "ASET_LIHAT", "FORECAST_LIHAT"],
  viewer: ["BUDGET_LIHAT", "ASET_LIHAT", "FORECAST_LIHAT"],
  perpajakan: [],
  gudang: [],
};

function useCan7D() {
  const { user } = useAuth();
  const role = user?.role;
  const perms = PERMS_7D[role] || [];
  const isSuper = role === "super_admin" || perms.includes("*");
  return (p) => isSuper || perms.includes(p);
}

function NoAccessBanner({ perm }) {
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900" data-testid="no-access-banner">
      <ShieldCheck className="inline mr-2" size={18} />
      Anda belum memiliki izin <b>{perm}</b> untuk membuka halaman ini.
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    DRAFT: "bg-slate-100 text-slate-700",
    REVIEW: "bg-amber-100 text-amber-800",
    APPROVED: "bg-emerald-100 text-emerald-800",
    LOCKED: "bg-indigo-100 text-indigo-800",
  };
  return <Badge className={map[status] || "bg-slate-100"} data-testid={`budget-status-${status}`}>{status}</Badge>;
}

/* ============ HUB ============ */
export function BudgetHub() {
  const can = useCan7D();
  const items = [
    { to: "/tahap7d/budget/list", icon: Layers, title: "Daftar Budget",
      desc: "Kelola budget tahunan (DRAFT → REVIEW → APPROVED → LOCKED)", perm: "BUDGET_LIHAT" },
    { to: "/tahap7d/budget/dashboard", icon: PieChart, title: "Dashboard Budget",
      desc: "Revenue, GP, OpEx, Net Profit vs Budget + Top Variance", perm: "BUDGET_LIHAT" },
    { to: "/tahap7d/forecast", icon: LineChart, title: "Financial Forecast",
      desc: "Forecast 3/6/12 bulan berbasis ACTUAL + BUDGET", perm: "FORECAST_LIHAT" },
    { to: "/tahap7d/depreciation-methods", icon: Calculator, title: "Metode Depresiasi Lanjutan",
      desc: "DDB, SYD, UOP + rebuild schedule tanpa merusak historical", perm: "ASET_LIHAT" },
    { to: "/tahap7d/exports", icon: FileSpreadsheet, title: "Ekspor Laporan 7D",
      desc: "PDF/XLSX untuk Budget vs Actual, Variance, Dashboard, Depreciation, Forecast", perm: "BUDGET_LIHAT" },
  ];
  return (
    <div className="space-y-4" data-testid="tahap7d-hub">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Financial Planning &amp; Advanced Controls</h1>
        <p className="text-sm text-slate-600">Tahap 7D — Budget, Depresiasi Lanjutan, Forecast &amp; Ekspor.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((it) => {
          const allowed = can(it.perm);
          return (
            <Card key={it.to} className={allowed ? "hover:shadow-md transition" : "opacity-50"}>
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  <div className="rounded-lg bg-indigo-50 p-3"><it.icon className="text-indigo-700" size={22} /></div>
                  <div className="flex-1">
                    <div className="text-base font-semibold text-slate-900">{it.title}</div>
                    <div className="text-sm text-slate-600 mt-1">{it.desc}</div>
                    {allowed ? (
                      <Link to={it.to} className="mt-3 inline-block text-sm text-indigo-700 hover:underline"
                        data-testid={`hub-link-${it.title.replaceAll(" ", "-")}`}>Buka →</Link>
                    ) : (
                      <div className="mt-3 text-xs text-amber-700">Perlu izin: {it.perm}</div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

/* ============ BUDGET LIST ============ */
export function BudgetListPage() {
  const can = useCan7D();
  const nav = useNavigate();
  const [year, setYear] = useState(new Date().getFullYear());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    if (!can("BUDGET_LIHAT")) return;
    setLoading(true);
    try {
      const r = await api.get(`/budget?year=${year}`);
      setRows(r.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year]);
  if (!can("BUDGET_LIHAT")) return <NoAccessBanner perm="BUDGET_LIHAT" />;
  return (
    <div className="space-y-4" data-testid="budget-list-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Daftar Budget</h1>
          <p className="text-sm text-slate-600">Kelola budget tahunan (DRAFT → REVIEW → APPROVED → LOCKED).</p>
        </div>
        <div className="flex items-center gap-2">
          <Label>Tahun</Label>
          <Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))}
                 className="w-24" data-testid="budget-year-filter" />
          {can("BUDGET_CREATE") && (
            <Button onClick={() => nav("/tahap7d/budget/new")} data-testid="btn-new-budget">
              <Plus size={16} className="mr-1" />Buat Budget
            </Button>
          )}
        </div>
      </div>
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Kode</TableHead>
                <TableHead>Nama</TableHead>
                <TableHead>Tahun</TableHead>
                <TableHead>Versi</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Total Budget</TableHead>
                <TableHead>Line</TableHead>
                <TableHead>Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? <TableRow><TableCell colSpan={8}>Memuat…</TableCell></TableRow>
              : rows.length === 0 ? <TableRow><TableCell colSpan={8} className="text-center text-slate-500">Belum ada budget untuk tahun {year}.</TableCell></TableRow>
              : rows.map((r) => (
                <TableRow key={r.id} data-testid={`budget-row-${r.id}`}>
                  <TableCell className="font-mono">{r.code}</TableCell>
                  <TableCell>{r.name}</TableCell>
                  <TableCell>{r.year}</TableCell>
                  <TableCell>v{r.version}</TableCell>
                  <TableCell><StatusBadge status={r.status} /></TableCell>
                  <TableCell className="text-right">{formatRupiah(r.total_budget)}</TableCell>
                  <TableCell>{r.line_count}</TableCell>
                  <TableCell className="space-x-1">
                    <Link to={`/tahap7d/budget/${r.id}`} className="text-sm text-indigo-700 hover:underline"
                      data-testid={`budget-view-${r.id}`}>Detail</Link>
                    <Link to={`/tahap7d/budget/${r.id}/variance`} className="text-sm text-emerald-700 hover:underline ml-2"
                      data-testid={`budget-variance-${r.id}`}>Variance</Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

/* ============ BUDGET EDITOR (CREATE/EDIT) ============ */
export function BudgetEditPage() {
  const can = useCan7D();
  const nav = useNavigate();
  const { id } = useParams();
  const isNew = !id;
  const [budget, setBudget] = useState({ code: "", name: "", year: new Date().getFullYear() + 1, notes: "" });
  const [lines, setLines] = useState([]);
  const [coas, setCoas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("DRAFT");
  const monthKeys = useMemo(() => Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0")), []);
  useEffect(() => {
    api.get("/masters/chart_of_accounts?limit=500").then((r) => {
      const list = Array.isArray(r.data) ? r.data : (r.data.items || []);
      setCoas(list);
    }).catch(() => {});
    if (id) {
      api.get(`/budget/${id}`).then((r) => {
        const d = r.data;
        setBudget({ code: d.code, name: d.name, year: d.year, notes: d.notes || "" });
        setLines((d.lines || []).map((l) => ({
          coa_code: l.coa_code, cost_center: l.cost_center || "",
          months: l.months || {}, notes: l.notes || "",
        })));
        setStatus(d.status);
      }).catch((e) => toast.error(formatApiError(e)));
    }
  }, [id]);
  const addLine = () => setLines([...lines, { coa_code: "", cost_center: "", months: {}, notes: "" }]);
  const removeLine = (idx) => setLines(lines.filter((_, i) => i !== idx));
  const setMonth = (idx, mk, v) => {
    const next = [...lines];
    next[idx] = { ...next[idx], months: { ...next[idx].months, [mk]: Number(v || 0) } };
    setLines(next);
  };
  const lineTotal = (l) => monthKeys.reduce((s, m) => s + Number(l.months?.[m] || 0), 0);
  const grandTotal = lines.reduce((s, l) => s + lineTotal(l), 0);
  const save = async () => {
    if (!budget.code || !budget.name) { toast.error("Kode & nama wajib diisi."); return; }
    if (lines.length === 0) { toast.error("Minimal 1 line."); return; }
    setLoading(true);
    try {
      const payload = { ...budget, lines };
      if (isNew) {
        const r = await api.post("/budget", payload);
        toast.success("Budget dibuat.");
        nav(`/tahap7d/budget/${r.data.id}`);
      } else {
        await api.put(`/budget/${id}`, {
          name: budget.name, notes: budget.notes, lines,
        });
        toast.success("Budget diperbarui.");
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  const doAction = async (action, extra = {}) => {
    try {
      const r = await api.post(`/budget/${id}/${action}`, extra);
      setStatus(r.data.status);
      toast.success(`Aksi ${action.toUpperCase()} berhasil.`);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  if (!can("BUDGET_LIHAT")) return <NoAccessBanner perm="BUDGET_LIHAT" />;
  const readOnly = !isNew && status !== "DRAFT";
  return (
    <div className="space-y-4" data-testid="budget-edit-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {isNew ? "Buat Budget Baru" : `Budget ${budget.code}`}
          </h1>
          <div className="text-sm text-slate-600">Status: {!isNew && <StatusBadge status={status} />}</div>
        </div>
        <div className="flex gap-2">
          {!isNew && status === "DRAFT" && can("BUDGET_EDIT") && (
            <Button onClick={() => doAction("submit")} variant="outline" data-testid="btn-submit-budget">
              <Send size={16} className="mr-1" />Submit ke Review
            </Button>
          )}
          {!isNew && status === "REVIEW" && can("BUDGET_APPROVE") && (
            <>
              <Button onClick={() => doAction("approve", { reason: "Setuju" })}
                      className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-approve-budget">
                <Check size={16} className="mr-1" />Setujui
              </Button>
              <Button variant="destructive" onClick={() => {
                const reason = prompt("Alasan tolak?"); if (reason) doAction("reject", { reason });
              }} data-testid="btn-reject-budget">
                <X size={16} className="mr-1" />Tolak
              </Button>
            </>
          )}
          {!isNew && status === "APPROVED" && can("BUDGET_LOCK") && (
            <Button onClick={() => doAction("lock")} className="bg-indigo-600 hover:bg-indigo-700"
                    data-testid="btn-lock-budget">
              <Lock size={16} className="mr-1" />Kunci
            </Button>
          )}
          {!isNew && (status === "APPROVED" || status === "LOCKED") && can("BUDGET_CREATE") && (
            <Button onClick={async () => {
              try {
                const r = await api.post(`/budget/${id}/revise`, {});
                toast.success(`Versi baru dibuat.`);
                nav(`/tahap7d/budget/${r.data.id}`);
              } catch (e) { toast.error(formatApiError(e)); }
            }} variant="outline" data-testid="btn-revise-budget">Revise (Versi Baru)</Button>
          )}
        </div>
      </div>
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>Kode</Label>
              <Input value={budget.code} onChange={(e) => setBudget({ ...budget, code: e.target.value })}
                     disabled={!isNew} data-testid="budget-code-input" />
            </div>
            <div>
              <Label>Nama</Label>
              <Input value={budget.name} onChange={(e) => setBudget({ ...budget, name: e.target.value })}
                     disabled={readOnly} data-testid="budget-name-input" />
            </div>
            <div>
              <Label>Tahun</Label>
              <Input type="number" value={budget.year}
                     onChange={(e) => setBudget({ ...budget, year: Number(e.target.value) })}
                     disabled={!isNew} data-testid="budget-year-input" />
            </div>
          </div>
          <div>
            <Label>Catatan</Label>
            <Textarea value={budget.notes || ""} onChange={(e) => setBudget({ ...budget, notes: e.target.value })}
                      disabled={readOnly} data-testid="budget-notes-input" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-900">Baris Budget (per COA)</h3>
            {!readOnly && can("BUDGET_EDIT") && (
              <Button size="sm" variant="outline" onClick={addLine} data-testid="btn-add-budget-line">
                <Plus size={14} className="mr-1" />Tambah Baris
              </Button>
            )}
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[220px]">COA</TableHead>
                  <TableHead className="min-w-[120px]">Cost Center</TableHead>
                  {monthKeys.map((m) => (<TableHead key={m} className="text-right min-w-[100px]">{m}</TableHead>))}
                  <TableHead className="text-right">Total</TableHead>
                  {!readOnly && <TableHead />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {lines.map((ln, idx) => (
                  <TableRow key={idx} data-testid={`budget-line-${idx}`}>
                    <TableCell>
                      <Select value={ln.coa_code || ""}
                              onValueChange={(v) => { const next = [...lines]; next[idx].coa_code = v; setLines(next); }}
                              disabled={readOnly}>
                        <SelectTrigger data-testid={`budget-line-coa-${idx}`}><SelectValue placeholder="Pilih COA" /></SelectTrigger>
                        <SelectContent className="max-h-[400px]">
                          {coas.map((c) => (
                            <SelectItem key={c.code} value={c.code}>
                              {c.code} — {c.name} ({c.category})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Input value={ln.cost_center || ""} placeholder="opsional"
                             onChange={(e) => { const next = [...lines]; next[idx].cost_center = e.target.value; setLines(next); }}
                             disabled={readOnly} data-testid={`budget-line-cc-${idx}`} />
                    </TableCell>
                    {monthKeys.map((m) => (
                      <TableCell key={m} className="text-right">
                        <Input type="number" className="text-right w-[110px]"
                               value={ln.months?.[m] || ""}
                               onChange={(e) => setMonth(idx, m, e.target.value)}
                               disabled={readOnly}
                               data-testid={`budget-line-${idx}-m${m}`} />
                      </TableCell>
                    ))}
                    <TableCell className="text-right font-medium">{formatRupiah(lineTotal(ln))}</TableCell>
                    {!readOnly && (
                      <TableCell>
                        <Button size="sm" variant="ghost" onClick={() => removeLine(idx)}
                                data-testid={`btn-remove-line-${idx}`}>
                          <X size={14} />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="mt-3 flex items-center justify-between">
            <div className="text-sm text-slate-600">Total Baris: {lines.length}</div>
            <div className="text-lg font-bold text-indigo-700">Grand Total: {formatRupiah(grandTotal)}</div>
          </div>
          {!readOnly && can("BUDGET_EDIT") && (
            <div className="mt-4 flex justify-end">
              <Button onClick={save} disabled={loading} data-testid="btn-save-budget">
                {loading ? "Menyimpan…" : (isNew ? "Buat Budget" : "Simpan Perubahan")}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* ============ VARIANCE ANALYSIS ============ */
export function BudgetVariancePage() {
  const can = useCan7D();
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [uptoMonth, setUptoMonth] = useState(new Date().getMonth() + 1);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/budget/${id}/variance?upto_month=${uptoMonth}`);
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (can("BUDGET_LIHAT")) load(); /* eslint-disable-next-line */ }, [id, uptoMonth]);
  const doExport = async (fmt) => {
    try {
      const r = await api.post("/exports/budget-vs-actual",
        { budget_id: id, format: fmt, upto_month: uptoMonth },
        { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `budget-vs-actual.${fmt}`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  if (!can("BUDGET_LIHAT")) return <NoAccessBanner perm="BUDGET_LIHAT" />;
  if (!data) return <div className="p-8 text-center text-slate-500">{loading ? "Memuat…" : "Belum ada data."}</div>;
  const s = data.summary;
  const kpiCard = (label, k, colorHint) => {
    const isRevOrGpNet = ["revenue", "gross_profit", "net_profit"].includes(colorHint);
    const positive = isRevOrGpNet ? k.variance >= 0 : k.variance <= 0;
    return (
      <Card><CardContent className="p-4">
        <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
        <div className="text-lg font-bold text-slate-900">{formatRupiah(k.actual)}</div>
        <div className="text-xs text-slate-600">Budget: {formatRupiah(k.budget)}</div>
        <div className={`text-xs font-medium mt-1 ${positive ? "text-emerald-700" : "text-rose-700"}`}>
          {positive ? <TrendingUp className="inline mr-1" size={12} /> : <TrendingDown className="inline mr-1" size={12} />}
          Variance: {formatRupiah(k.variance)}
        </div>
      </CardContent></Card>
    );
  };
  return (
    <div className="space-y-4" data-testid="budget-variance-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Variance Analysis — {data.budget.code}</h1>
          <p className="text-sm text-slate-600">{data.budget.name} · Tahun {data.budget.year} · s/d bulan {String(data.upto_month).padStart(2, "0")}</p>
        </div>
        <div className="flex items-center gap-2">
          <Label>s/d Bulan</Label>
          <Input type="number" min="1" max="12" value={uptoMonth}
                 onChange={(e) => setUptoMonth(Number(e.target.value))}
                 className="w-20" data-testid="variance-upto-month" />
          <Button variant="outline" onClick={() => doExport("xlsx")} data-testid="btn-export-variance-xlsx">
            <Download size={14} className="mr-1" />XLSX
          </Button>
          <Button variant="outline" onClick={() => doExport("pdf")} data-testid="btn-export-variance-pdf">
            <Download size={14} className="mr-1" />PDF
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {kpiCard("Revenue", s.revenue, "revenue")}
        {kpiCard("COGS", s.cogs, "cogs")}
        {kpiCard("Gross Profit", s.gross_profit, "gross_profit")}
        {kpiCard("OpEx", s.opex, "opex")}
        {kpiCard("Net Profit", s.net_profit, "net_profit")}
      </div>
      <Card><CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>COA</TableHead>
                <TableHead>Nama</TableHead>
                <TableHead>Kategori</TableHead>
                <TableHead>Signage</TableHead>
                <TableHead className="text-right">Total Budget</TableHead>
                <TableHead className="text-right">Total Actual</TableHead>
                <TableHead className="text-right">Variance</TableHead>
                <TableHead className="text-right">Favorable</TableHead>
                <TableHead className="text-right">%</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.lines.map((ln) => {
                const v = ln.variance;
                return (
                  <TableRow key={ln.line_id} data-testid={`variance-row-${ln.coa_code}`}>
                    <TableCell className="font-mono">{ln.coa_code}</TableCell>
                    <TableCell>{ln.coa_name}</TableCell>
                    <TableCell>{ln.coa_category}</TableCell>
                    <TableCell><Badge variant="outline">{ln.signage}</Badge></TableCell>
                    <TableCell className="text-right">{formatRupiah(v.total_budget)}</TableCell>
                    <TableCell className="text-right">{formatRupiah(v.total_actual)}</TableCell>
                    <TableCell className="text-right">{formatRupiah(v.total_variance)}</TableCell>
                    <TableCell className={`text-right ${v.total_favorable >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                      {formatRupiah(v.total_favorable)}
                    </TableCell>
                    <TableCell className="text-right">{v.total_variance_pct.toFixed(1)}%</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent></Card>
    </div>
  );
}

/* ============ BUDGET DASHBOARD ============ */
export function BudgetDashboardPage() {
  const can = useCan7D();
  const [year, setYear] = useState(new Date().getFullYear());
  const [uptoMonth, setUptoMonth] = useState(new Date().getMonth() + 1);
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/budget/dashboard/summary?year=${year}&upto_month=${uptoMonth}`);
      setD(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (can("BUDGET_LIHAT")) load(); /* eslint-disable-next-line */ }, [year, uptoMonth]);
  if (!can("BUDGET_LIHAT")) return <NoAccessBanner perm="BUDGET_LIHAT" />;
  if (!d) return <div className="p-8 text-center text-slate-500">{loading ? "Memuat…" : "Belum ada data."}</div>;
  return (
    <div className="space-y-4" data-testid="budget-dashboard-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Management Budget Dashboard</h1>
          <p className="text-sm text-slate-600">YTD s/d bulan {String(uptoMonth).padStart(2, "0")} tahun {year}</p>
        </div>
        <div className="flex items-center gap-2">
          <Label>Tahun</Label>
          <Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))}
                 className="w-24" data-testid="dashboard-year" />
          <Label>s/d Bulan</Label>
          <Input type="number" min="1" max="12" value={uptoMonth}
                 onChange={(e) => setUptoMonth(Number(e.target.value))}
                 className="w-20" data-testid="dashboard-upto" />
          <Button variant="outline" onClick={async () => {
            const r = await api.post(`/exports/budget-dashboard?year=${year}&upto_month=${uptoMonth}&format=xlsx`,
                                     null, { responseType: "blob" });
            const a = document.createElement("a"); a.href = URL.createObjectURL(r.data);
            a.download = "budget-dashboard.xlsx"; a.click();
          }} data-testid="btn-export-dashboard">
            <Download size={14} className="mr-1" />Ekspor
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {["revenue", "cogs", "gross_profit", "opex", "net_profit"].map((k) => {
          const kk = d.kpi[k];
          return (
            <Card key={k}><CardContent className="p-4">
              <div className="text-xs uppercase tracking-wide text-slate-500">
                {k.replace("_", " ")}
              </div>
              <div className="text-lg font-bold text-slate-900">{formatRupiah(kk.actual)}</div>
              <div className="text-xs text-slate-600">Budget: {formatRupiah(kk.budget)}</div>
              <div className="text-xs font-medium mt-1">
                Variance: {formatRupiah(kk.variance)}
              </div>
            </CardContent></Card>
          );
        })}
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Card><CardContent className="p-4">
          <div className="text-xs uppercase text-slate-500">Cash Position</div>
          <div className="text-lg font-bold">{formatRupiah(d.cash_position.total)}</div>
          <div className="text-xs text-slate-600">Bank: {formatRupiah(d.cash_position.bank)} · Kas: {formatRupiah(d.cash_position.cash)}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs uppercase text-slate-500">AR / AP Outstanding</div>
          <div className="text-lg font-bold">{formatRupiah(d.ar_outstanding - d.ap_outstanding)}</div>
          <div className="text-xs text-slate-600">AR: {formatRupiah(d.ar_outstanding)} · AP: {formatRupiah(d.ap_outstanding)}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs uppercase text-slate-500">Inventory Value</div>
          <div className="text-lg font-bold">{formatRupiah(d.inventory_value)}</div>
        </CardContent></Card>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {(() => {
          const monthly = (d.monthly || []).map((m) => ({ name: m.month, Budget: Number(m.budget || 0), Actual: Number(m.actual || 0) }));
          const cats = (d.categories || []).slice(0, 8).map((c) => ({
            name: c.category, favorable: Number(c.favorable || 0),
          }));
          return (
            <>
              <BarCard
                title="Budget vs Actual per Bulan"
                testId="chart-budget-monthly"
                hint="Belum ada budget disetujui / angka actual untuk periode ini."
                currency
                data={monthly}
                bars={[
                  { key: "Budget", label: "Budget", color: CHART_COLORS.baseline },
                  { key: "Actual", label: "Actual", color: CHART_COLORS.positive },
                ]}
              />
              <HorizontalBarCard
                title="Variance per Kategori (Favorable)"
                testId="chart-budget-variance-category"
                hint="Belum ada kategori budget tercatat."
                currency
                data={cats}
                bars={[{ key: "favorable", label: "Favorable", color: CHART_COLORS.info }]}
                height={Math.max(220, cats.length * 42 + 60)}
              />
            </>
          );
        })()}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card><CardContent className="p-4">
          <h3 className="font-semibold mb-3 text-emerald-700">Top Favorable Variance</h3>
          <Table>
            <TableHeader><TableRow>
              <TableHead>COA</TableHead><TableHead>Nama</TableHead>
              <TableHead className="text-right">Favorable</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {(d.top_favorable || []).map((r, i) => (
                <TableRow key={i}>
                  <TableCell className="font-mono">{r.coa_code}</TableCell>
                  <TableCell>{r.coa_name}</TableCell>
                  <TableCell className="text-right text-emerald-700">{formatRupiah(r.favorable)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <h3 className="font-semibold mb-3 text-rose-700">Top Unfavorable Variance</h3>
          <Table>
            <TableHeader><TableRow>
              <TableHead>COA</TableHead><TableHead>Nama</TableHead>
              <TableHead className="text-right">Favorable</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {(d.top_unfavorable || []).map((r, i) => (
                <TableRow key={i}>
                  <TableCell className="font-mono">{r.coa_code}</TableCell>
                  <TableCell>{r.coa_name}</TableCell>
                  <TableCell className="text-right text-rose-700">{formatRupiah(r.favorable)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent></Card>
      </div>
    </div>
  );
}

/* ============ FORECAST ============ */
export function ForecastPage() {
  const can = useCan7D();
  const [horizon, setHorizon] = useState(3);
  const [method, setMethod] = useState("HYBRID");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/forecast/generate?horizon=${horizon}&method=${method}`);
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (can("FORECAST_LIHAT")) load(); /* eslint-disable-next-line */ }, [horizon, method]);
  const doExport = async (fmt) => {
    const r = await api.post("/exports/forecast", { horizon, method, format: fmt },
                              { responseType: "blob" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(r.data);
    a.download = `forecast.${fmt}`; a.click();
  };
  if (!can("FORECAST_LIHAT")) return <NoAccessBanner perm="FORECAST_LIHAT" />;
  return (
    <div className="space-y-4" data-testid="forecast-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Financial Forecast Foundation</h1>
          <p className="text-sm text-slate-600">Forecast berdasarkan ACTUAL &amp; BUDGET — <b>bukan prediksi AI</b>.</p>
        </div>
        <div className="flex items-center gap-2">
          <Label>Horizon</Label>
          <Select value={String(horizon)} onValueChange={(v) => setHorizon(Number(v))}>
            <SelectTrigger className="w-24" data-testid="forecast-horizon"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="3">3 bln</SelectItem>
              <SelectItem value="6">6 bln</SelectItem>
              <SelectItem value="12">12 bln</SelectItem>
            </SelectContent>
          </Select>
          <Label>Method</Label>
          <Select value={method} onValueChange={setMethod}>
            <SelectTrigger className="w-40" data-testid="forecast-method"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="HYBRID">HYBRID</SelectItem>
              <SelectItem value="BUDGET">BUDGET only</SelectItem>
              <SelectItem value="TRAILING_AVG">TRAILING AVG</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => doExport("xlsx")} data-testid="btn-forecast-xlsx">
            <Download size={14} className="mr-1" />XLSX
          </Button>
          <Button variant="outline" onClick={() => doExport("pdf")} data-testid="btn-forecast-pdf">
            <Download size={14} className="mr-1" />PDF
          </Button>
        </div>
      </div>
      {data && (
        <>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
            <b>Cut-off:</b> {data.cutoff_month} · <b>Method:</b> {data.method} · <b>Horizon:</b> {data.horizon} bulan
            <br /><i>{data.disclaimer}</i>
          </div>
          <Card><CardContent className="p-4">
            <h3 className="font-semibold mb-3">KPI Ringkasan (per bulan future)</h3>
            <Table>
              <TableHeader><TableRow>
                <TableHead>Bulan</TableHead>
                <TableHead className="text-right">Revenue</TableHead>
                <TableHead className="text-right">COGS</TableHead>
                <TableHead className="text-right">OpEx</TableHead>
                <TableHead className="text-right">Gross Profit</TableHead>
                <TableHead className="text-right">Net Profit</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {(data.kpi_summary || []).map((k) => (
                  <TableRow key={k.month} data-testid={`forecast-row-${k.month}`}>
                    <TableCell>{k.month}</TableCell>
                    <TableCell className="text-right">{formatRupiah(k.revenue)}</TableCell>
                    <TableCell className="text-right">{formatRupiah(k.cogs)}</TableCell>
                    <TableCell className="text-right">{formatRupiah(k.opex)}</TableCell>
                    <TableCell className="text-right">{formatRupiah(k.gross_profit)}</TableCell>
                    <TableCell className="text-right font-semibold">{formatRupiah(k.net_profit)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </>
      )}
    </div>
  );
}

/* ============ ADVANCED DEPRECIATION METHODS ============ */
export function DepreciationMethodsPage() {
  const can = useCan7D();
  const [methods, setMethods] = useState([]);
  const [assets, setAssets] = useState([]);
  const [selected, setSelected] = useState(null);
  useEffect(() => {
    if (!can("ASET_LIHAT")) return;
    api.get("/asset/methods").then((r) => setMethods(r.data)).catch(() => {});
    api.get("/asset/register").then((r) => setAssets(r.data)).catch(() => {});
    /* eslint-disable-next-line */
  }, []);
  const loadSchedule = async (assetId) => {
    try {
      const r = await api.get(`/asset/depreciation-schedule/${assetId}`);
      setSelected(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  if (!can("ASET_LIHAT")) return <NoAccessBanner perm="ASET_LIHAT" />;
  return (
    <div className="space-y-4" data-testid="depreciation-methods-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Metode Depresiasi Lanjutan</h1>
        <p className="text-sm text-slate-600">Straight Line, DDB, SYD, Units of Production. Semua metode memakai mapping COA dari kategori aset.</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {methods.map((m) => (
          <Card key={m.code}><CardContent className="p-4">
            <div className="font-semibold text-slate-900">{m.name}</div>
            <div className="text-xs text-slate-600 mt-1">{m.description}</div>
            <div className="text-xs text-indigo-700 mt-2">{m.code}</div>
          </CardContent></Card>
        ))}
      </div>
      <Card><CardContent className="p-0">
        <div className="p-4 border-b"><h3 className="font-semibold">Aset & Metode</h3></div>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Kode</TableHead><TableHead>Nama</TableHead>
            <TableHead>Kategori</TableHead><TableHead>Metode</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Nilai Buku</TableHead>
            <TableHead>Aksi</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {assets.map((a) => (
              <TableRow key={a.id} data-testid={`asset-row-${a.id}`}>
                <TableCell className="font-mono">{a.code}</TableCell>
                <TableCell>{a.name}</TableCell>
                <TableCell>{a.category_code}</TableCell>
                <TableCell><Badge variant="outline">{a.depreciation_method}</Badge></TableCell>
                <TableCell>{a.status}</TableCell>
                <TableCell className="text-right">{formatRupiah(a.current_book_value)}</TableCell>
                <TableCell>
                  <Button size="sm" variant="ghost" onClick={() => loadSchedule(a.id)} data-testid={`btn-view-schedule-${a.id}`}>
                    <Eye size={14} className="mr-1" />Schedule
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>
      {selected && (
        <Card><CardContent className="p-4">
          <h3 className="font-semibold mb-2">Schedule — {selected.asset.code} ({selected.asset.depreciation_method})</h3>
          <div className="text-xs text-slate-600 mb-3">
            Cost: {formatRupiah(selected.asset.acquisition_cost)} ·
            Akm. Dep: {formatRupiah(selected.asset.accumulated_depreciation)} ·
            NB: {formatRupiah(selected.asset.current_book_value)}
          </div>
          <div className="max-h-96 overflow-y-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Seq</TableHead><TableHead>Periode</TableHead>
                <TableHead>Metode</TableHead>
                <TableHead className="text-right">Depresiasi</TableHead>
                <TableHead className="text-right">Akumulasi</TableHead>
                <TableHead className="text-right">Nilai Buku</TableHead>
                <TableHead>Posted</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {selected.schedule.map((r, i) => (
                  <TableRow key={i}>
                    <TableCell>{r.seq}</TableCell>
                    <TableCell>{r.period}</TableCell>
                    <TableCell><Badge variant="outline">{r.method}</Badge></TableCell>
                    <TableCell className="text-right">{formatRupiah(r.depreciation)}</TableCell>
                    <TableCell className="text-right">{formatRupiah(r.accumulated)}</TableCell>
                    <TableCell className="text-right">{formatRupiah(r.book_value)}</TableCell>
                    <TableCell>{r.posted ? <Badge className="bg-emerald-100 text-emerald-800">POSTED</Badge> : <Badge variant="outline">PENDING</Badge>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent></Card>
      )}
    </div>
  );
}

/* ============ EXPORTS 7D HUB ============ */
export function Exports7DHub() {
  const can = useCan7D();
  const [budgets, setBudgets] = useState([]);
  const [budgetId, setBudgetId] = useState("");
  useEffect(() => {
    if (!can("BUDGET_LIHAT")) return;
    api.get("/budget").then((r) => setBudgets(r.data || [])).catch(() => {});
    /* eslint-disable-next-line */
  }, []);
  const download = async (url, body, filename) => {
    try {
      const r = await api.post(url, body, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data); a.download = filename; a.click();
      toast.success("Berhasil diekspor.");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  if (!can("BUDGET_LIHAT")) return <NoAccessBanner perm="BUDGET_LIHAT" />;
  return (
    <div className="space-y-4" data-testid="exports7d-hub">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Ekspor Laporan 7D</h1>
        <p className="text-sm text-slate-600">PDF/XLSX untuk Budget vs Actual, Variance, Dashboard, Depreciation, Forecast.</p>
      </div>
      <Card><CardContent className="p-4 space-y-3">
        <h3 className="font-semibold">Budget vs Actual / Variance</h3>
        <div className="flex items-center gap-2">
          <Label>Budget</Label>
          <Select value={budgetId} onValueChange={setBudgetId}>
            <SelectTrigger className="w-96" data-testid="export-budget-select"><SelectValue placeholder="Pilih budget…" /></SelectTrigger>
            <SelectContent>
              {budgets.map((b) => (
                <SelectItem key={b.id} value={b.id}>{b.code} — {b.name} (v{b.version}, {b.status})</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" disabled={!budgetId}
                  onClick={() => download("/exports/budget-vs-actual",
                                          { budget_id: budgetId, format: "xlsx" },
                                          "budget-vs-actual.xlsx")}
                  data-testid="btn-export-bvsa-xlsx">
            <Download size={14} className="mr-1" />BvA XLSX
          </Button>
          <Button variant="outline" disabled={!budgetId}
                  onClick={() => download("/exports/budget-vs-actual",
                                          { budget_id: budgetId, format: "pdf" },
                                          "budget-vs-actual.pdf")}
                  data-testid="btn-export-bvsa-pdf">
            <Download size={14} className="mr-1" />BvA PDF
          </Button>
          <Button variant="outline" disabled={!budgetId}
                  onClick={() => download("/exports/variance-analysis",
                                          { budget_id: budgetId, format: "xlsx" },
                                          "variance-analysis.xlsx")}
                  data-testid="btn-export-variance-x">
            <Download size={14} className="mr-1" />Variance XLSX
          </Button>
        </div>
      </CardContent></Card>
      <Card><CardContent className="p-4 space-y-3">
        <h3 className="font-semibold">Fixed Asset Depreciation Schedule</h3>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => download("/exports/depreciation-schedule",
                                    { format: "xlsx" }, "depreciation-schedule.xlsx")}
                  data-testid="btn-export-dep-x">
            <Download size={14} className="mr-1" />XLSX
          </Button>
          <Button variant="outline" onClick={() => download("/exports/depreciation-schedule",
                                    { format: "pdf" }, "depreciation-schedule.pdf")}
                  data-testid="btn-export-dep-p">
            <Download size={14} className="mr-1" />PDF
          </Button>
        </div>
      </CardContent></Card>
      <Card><CardContent className="p-4 space-y-3">
        <h3 className="font-semibold">Forecast (3 bulan HYBRID)</h3>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => download("/exports/forecast",
                                    { horizon: 3, method: "HYBRID", format: "xlsx" },
                                    "forecast.xlsx")}
                  data-testid="btn-export-fc-x">
            <Download size={14} className="mr-1" />XLSX
          </Button>
          <Button variant="outline" onClick={() => download("/exports/forecast",
                                    { horizon: 3, method: "HYBRID", format: "pdf" },
                                    "forecast.pdf")}
                  data-testid="btn-export-fc-p">
            <Download size={14} className="mr-1" />PDF
          </Button>
        </div>
      </CardContent></Card>
    </div>
  );
}

export default {
  BudgetHub, BudgetListPage, BudgetEditPage, BudgetVariancePage,
  BudgetDashboardPage, ForecastPage, DepreciationMethodsPage, Exports7DHub,
};
